import styled from 'styled-components';

export const TitleBar = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
  min-width: 0;
  flex-direction: ${(p) => (p.isMobile ? 'column' : 'row')};
  font-size: ${(p) => (p.isMobile ? p.theme.fontSizes.md : p.theme.fontSizes.lg)};

  @media (max-width: 639px) {
    gap: 10px;
    margin-bottom: 16px;

    > span {
      width: 100%;
      min-width: 0;
      text-align: center;
      overflow-wrap: anywhere;
    }
  }

  strong {
    font-weight: 700;
  }
`;

export const NotificationsShell = styled.div`
  width: 100%;
  min-width: 0;
`;

export const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;

  @media (max-width: 639px) {
    gap: 8px;
  }
`;

export const FilterButton = styled.button<{ active?: boolean }>`
  min-width: 88px;
  height: 42px;
  padding: 0 20px;
  border: 0;
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => (p.active ? p.theme.colors.secondary500 : p.theme.colors.gray200)};
  color: ${(p) => p.theme.colors.light};
  font-size: ${(p) => p.theme.fontSizes.md};
  cursor: pointer;

  @media (max-width: 639px) {
    min-width: 0;
    height: 40px;
    padding: 0 16px;
    font-size: 16px;
  }
`;

export const NotificationTableCard = styled.div`
  width: 100%;
  min-width: 0;
  padding: 30px 42px 24px;
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.light};

  @media (max-width: 900px) {
    padding: 22px 16px;
  }

  @media (max-width: 639px) {
    padding: 0;
    border-radius: 0;
    background: transparent;
  }
`;

export const NotificationTableScroll = styled.div`
  width: 100%;
  min-width: 0;
  overflow-x: auto;

  @media (max-width: 639px) {
    overflow: visible;
  }
`;

export const NotificationTable = styled.table`
  width: 100%;
  min-width: 820px;
  border-collapse: collapse;

  th,
  td {
    padding: 14px 12px;
    border-bottom: 1px solid ${(p) => p.theme.colors.gray100};
    text-align: left;
    font-size: ${(p) => p.theme.fontSizes.base};
    vertical-align: top;
  }

  th {
    color: ${(p) => p.theme.colors.gray300};
    font-weight: 400;
  }

  td {
    color: ${(p) => p.theme.colors.gray500};
  }

  tbody tr[data-unread='true'] {
    background: ${(p) => p.theme.colors.gray100};
  }

  @media (max-width: 639px) {
    display: block;
    min-width: 0;

    thead {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    tbody {
      display: grid;
      gap: 12px;
    }

    tbody tr {
      display: block;
      padding: 18px 12px;
      border-radius: 12px;
      background: ${(p) => p.theme.colors.light};
    }

    tbody tr:nth-child(odd) {
      background: ${(p) => p.theme.colors.gray000};
    }

    tbody tr[data-unread='true'] {
      border-left: 4px solid ${(p) => p.theme.colors.primary300};
      background: ${(p) => p.theme.colors.gray000};
    }

    th,
    td {
      height: auto;
      padding: 4px 0;
      border: 0;
      font-size: 14px;
      line-height: 1.3;
    }

    td {
      display: grid;
      grid-template-columns: 108px minmax(0, 1fr);
      gap: 12px;
      overflow-wrap: anywhere;
    }

    td::before {
      content: attr(data-label);
      color: ${(p) => p.theme.colors.gray300};
    }

    td[colspan] {
      display: block;
      padding: 18px 0;
      color: ${(p) => p.theme.colors.gray300};
      text-align: center;
    }

    td[colspan]::before {
      content: none;
    }
  }
`;

export const ActionCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  @media (max-width: 639px) {
    display: grid;
    grid-template-columns: 1fr;

    button {
      width: 100%;
      margin: 0;
    }
  }
`;

export const ActionButton = styled.button`
  min-height: 34px;
  padding: 0 12px;
  border: 0;
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.primary500};
  color: ${(p) => p.theme.colors.light};
  font-size: ${(p) => p.theme.fontSizes.sm};
  cursor: pointer;
  margin: 0 4px 4px 0;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

export const TableFooter = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 18px;
  color: ${(p) => p.theme.colors.gray300};
  flex-direction: ${(p) => (p.isMobile ? 'column' : 'row')};

  @media (max-width: 639px) {
    gap: 10px;
    font-size: 14px;
    text-align: center;
  }
`;

export const PaginationGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 639px) {
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
  }
`;

export const PageButton = styled.button<{ active?: boolean }>`
  min-width: 34px;
  height: 30px;
  border: 0;
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => (p.active ? p.theme.colors.primary300 : p.theme.colors.gray100)};
  color: ${(p) => (p.active ? p.theme.colors.light : p.theme.colors.gray500)};
  cursor: pointer;
`;
